import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  ButtonBase,
  Dialog,
  Button,
  Grid,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import ResponsiveSheet from '../shared/ResponsiveSheet';
import PointsAuditMode from './PointsAuditMode';
import { TOKENS, fmtMoney, pointTier } from '../theme';
import { detectPointDuplicates } from '../shared/duplicateDetection';
import {
  localDecisionsSelector,
  ownerSettingsSelector,
} from '../../../redux/selectors/employeeSessionV2';
import { recordLocalDecision } from '../../../redux/actions/employeeSessionV2';

// Compact machine pill — casino icon + machine number, used on every
// points row (main list + denomination drill-down sheet).
function MachineTag({ machine }) {
  if (!machine) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.375,
        borderRadius: 999,
        background: TOKENS.blue50,
        border: `1px solid ${TOKENS.blue200}`,
        flexShrink: 0,
      }}
    >
      <DesktopWindowsIcon sx={{ fontSize: 13, color: TOKENS.blue700 }} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'monospace',
          color: TOKENS.blue700,
          lineHeight: 1,
        }}
      >
        #{machine}
      </Typography>
    </Box>
  );
}

// Tiny approve/flag badge shown on photo-bearing rows once a decision exists.
function DecisionDot({ decision }) {
  if (!decision) return null;
  const approved = decision === 'APPROVED';
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        flexShrink: 0,
        background: approved ? TOKENS.emerald600 : TOKENS.red600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {approved ? (
        <CheckIcon sx={{ color: '#fff', fontSize: 12 }} />
      ) : (
        <CloseIcon sx={{ color: '#fff', fontSize: 12 }} />
      )}
    </Box>
  );
}

// Points Report. Display is read-only (no edit mode), but photo-bearing
// entries — VIP-match or Points Watching photos — go through the same
// approve/flag audit verification as bonuses. The audit tracker and audit
// mode only appear when at least one entry carries a photo.
export default function PointsDetail({
  sessionData,
  onSubmitDecision,
  onAuditComplete,
  // Force the audit tracker even if the owner hasn't enabled
  // "require review" — used in the reading-close flow.
  forceAuditTracker = false,
}) {
  const dispatch = useDispatch();
  const [openDenom, setOpenDenom] = useState(null);
  const [vipPhotoUrl, setVipPhotoUrl] = useState(null);
  const [auditIndex, setAuditIndex] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);

  const decisions = useSelector(localDecisionsSelector);
  const ownerSettings = useSelector(ownerSettingsSelector);

  const points = sessionData?.points || [];
  const total = points.reduce((s, p) => s + Number(p.points || 0), 0);

  // The audit applies to entries with a photo. Entries without a photo stay
  // informational and never gate the session close.
  const auditPoints = useMemo(() => points.filter((p) => p.hasPhoto), [points]);
  const duplicateFlags = useMemo(
    () => detectPointDuplicates(auditPoints),
    [auditPoints]
  );

  const pendingIdSet = new Set(auditPoints.map((p) => p.pendingId));
  const pointDecisions = Object.entries(decisions)
    .filter(
      ([k]) =>
        k.startsWith('POINT:') && pendingIdSet.has(k.slice('POINT:'.length))
    )
    .reduce((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {});
  const reviewedCount = Object.keys(pointDecisions).length;
  const approvedCount = Object.values(pointDecisions).filter(
    (d) => d === 'APPROVED'
  ).length;
  const flaggedCount = Object.values(pointDecisions).filter(
    (d) => d === 'FLAGGED'
  ).length;
  const remainingCount = auditPoints.length - reviewedCount;
  const auditComplete =
    reviewedCount === auditPoints.length && auditPoints.length > 0;
  const showAuditTracker =
    (ownerSettings?.requireReviewEveryBonus || forceAuditTracker) &&
    auditPoints.length > 0;

  const openAudit = (idx) => {
    setAuditIndex(idx);
    setAuditOpen(true);
  };
  const handleDecision = (point, decision) => {
    dispatch(recordLocalDecision('POINT', point.pendingId, decision));
    if (onSubmitDecision) onSubmitDecision(point, decision);
  };
  const handleAuditComplete = () => {
    setAuditOpen(false);
    if (onAuditComplete) onAuditComplete('Points audit completed');
  };

  // Denomination breakdown for the tappable chips
  const denoms = useMemo(() => {
    const m = {};
    points.forEach((p) => {
      const v = Number(p.points || 0);
      if (!m[v]) m[v] = [];
      m[v].push(p);
    });
    return Object.entries(m)
      .map(([v, entries]) => ({
        value: Number(v),
        count: entries.length,
        entries,
      }))
      .sort((a, b) => a.value - b.value);
  }, [points]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1.5 }}>
      {/* Hero summary */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.emerald200}`,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: TOKENS.slate500,
            }}
          >
            Total Points Given
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              fontFamily: 'monospace',
              color: TOKENS.slate900,
            }}
          >
            {fmtMoney(total)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: TOKENS.slate500, mt: 0.25 }}>
            {points.length} matches
          </Typography>
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: TOKENS.emerald50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <StarIcon sx={{ color: TOKENS.emerald700, fontSize: 24 }} />
        </Box>
      </Box>

      {/* Audit tracker — only for photo-bearing entries */}
      {showAuditTracker && (
        <Box
          sx={{
            background: auditComplete ? TOKENS.emerald50 : '#fff',
            border: `1px solid ${
              auditComplete ? TOKENS.emerald300 : TOKENS.slate200
            }`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: auditComplete
                    ? TOKENS.emerald600
                    : TOKENS.slate900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {auditComplete ? (
                  <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                ) : (
                  <VisibilityIcon sx={{ color: '#fff', fontSize: 16 }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {auditComplete ? 'Audit complete' : 'Photo review required'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                  {reviewedCount} of {auditPoints.length} reviewed ·{' '}
                  {remainingCount} remaining
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(reviewedCount / Math.max(1, auditPoints.length)) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 1,
                background: TOKENS.slate200,
                '& .MuiLinearProgress-bar': { background: TOKENS.emerald500 },
              }}
            />
            <Grid container spacing={1}>
              <Stat
                label="Approved"
                value={approvedCount}
                color={TOKENS.emerald700}
              />
              <Stat
                label="Flagged"
                value={flaggedCount}
                color={TOKENS.red700}
              />
              <Stat
                label="Remaining"
                value={remainingCount}
                color={TOKENS.slate700}
              />
            </Grid>
          </Box>
          <Button
            fullWidth
            onClick={() =>
              openAudit(
                Math.max(
                  0,
                  auditPoints.findIndex(
                    (p) => !pointDecisions[`POINT:${p.pendingId}`]
                  )
                )
              )
            }
            endIcon={<ChevronRightIcon />}
            sx={{
              py: 1.5,
              borderRadius: 0,
              background: auditComplete ? TOKENS.emerald700 : TOKENS.slate900,
              color: '#fff',
              justifyContent: 'space-between',
              px: 2,
              '&:hover': {
                background: auditComplete ? TOKENS.emerald900 : TOKENS.slate700,
              },
            }}
          >
            {reviewedCount === 0
              ? 'Start Audit'
              : auditComplete
              ? 'Review Audit'
              : 'Continue Audit'}
          </Button>
        </Box>
      )}

      {/* Duplicate alert */}
      {duplicateFlags.size > 0 && (
        <Box
          sx={{
            background: TOKENS.red50,
            border: `1px solid ${TOKENS.red200}`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: TOKENS.red600, fontSize: 20 }} />
          <Box>
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.red900 }}
            >
              {duplicateFlags.size} possible duplicate
              {duplicateFlags.size > 1 ? 's' : ''} detected
            </Typography>
            <Typography sx={{ fontSize: 11, color: TOKENS.red700 }}>
              Point photos with strong reuse signals — review carefully before
              approving.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Denomination chips */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          p: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: TOKENS.slate500,
            mb: 1,
          }}
        >
          Breakdown by Amount · Tap to view
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {denoms.map((d) => {
            const t = pointTier(d.value);
            return (
              <ButtonBase
                key={d.value}
                onClick={() => setOpenDenom(d)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  background: '#fff',
                  border: `1px solid ${TOKENS.slate200}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: t.color,
                  }}
                >
                  ${d.value}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TOKENS.slate500 }}>
                  ×
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                  }}
                >
                  {d.count}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* Points list */}
      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {points.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: TOKENS.slate400,
              fontSize: 13,
            }}
          >
            No points matches for this session.
          </Box>
        )}
        {points.map((p, i) => {
          const t = pointTier(p.points);
          const dec = pointDecisions[`POINT:${p.pendingId}`];
          const flag = p.hasPhoto ? duplicateFlags.get(p.pendingId) : null;
          const auditIdx = p.hasPhoto
            ? auditPoints.findIndex((ap) => ap.pendingId === p.pendingId)
            : -1;
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                borderBottom:
                  i < points.length - 1
                    ? `1px solid ${TOKENS.slate100}`
                    : 'none',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 40,
                  background:
                    flag && !dec
                      ? flag.severity === 'high'
                        ? TOKENS.red600
                        : TOKENS.amber600
                      : t.dot,
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: TOKENS.slate900 }}
                  noWrap
                >
                  {p.customer}
                </Typography>
                <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                  {p.signInTime || p.time || ''}
                  {flag && !dec && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        fontWeight: 700,
                        color:
                          flag.severity === 'high'
                            ? TOKENS.red700
                            : TOKENS.amber700,
                      }}
                    >
                      · possible duplicate
                    </Box>
                  )}
                </Typography>
              </Box>
              <MachineTag machine={p.machine} />
              <DecisionDot decision={dec} />
              {p.hasPhoto && auditIdx >= 0 ? (
                <IconButton
                  size="small"
                  onClick={() => openAudit(auditIdx)}
                  title="Review point photo"
                  sx={{ color: TOKENS.emerald700, p: 0.5 }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : (
                p.vipPhotoUrl && (
                  <IconButton
                    size="small"
                    onClick={() => setVipPhotoUrl(p.vipPhotoUrl)}
                    title="View VIP match photo"
                    sx={{ color: TOKENS.emerald700, p: 0.5 }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: t.color,
                  width: 56,
                  textAlign: 'right',
                }}
              >
                ${p.points}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <ResponsiveSheet open={!!openDenom} onClose={() => setOpenDenom(null)}>
        {openDenom && (
          <Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.slate200}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: TOKENS.slate500,
                  }}
                >
                  Customers who received
                </Typography>
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: pointTier(openDenom.value).color,
                    }}
                  >
                    ${openDenom.value}
                  </span>
                  <span style={{ color: TOKENS.slate500 }}>
                    · {openDenom.count}{' '}
                    {openDenom.count === 1 ? 'match' : 'matches'}
                  </span>
                </Typography>
              </Box>
              <IconButton onClick={() => setOpenDenom(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {openDenom.entries.map((p, i) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: `1px solid ${TOKENS.slate100}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                      {p.customer}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: TOKENS.slate500 }}>
                      {p.signInTime || p.time || ''}
                    </Typography>
                  </Box>
                  <MachineTag machine={p.machine} />
                  <DecisionDot
                    decision={pointDecisions[`POINT:${p.pendingId}`]}
                  />
                  {(p.photoUrl || p.vipPhotoUrl) && (
                    <IconButton
                      size="small"
                      onClick={() =>
                        setVipPhotoUrl(p.photoUrl || p.vipPhotoUrl)
                      }
                      title="View point photo"
                      sx={{ color: TOKENS.emerald700, p: 0.5 }}
                    >
                      <PhotoCameraIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${TOKENS.slate200}`,
                background: TOKENS.slate50,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.slate700 }}
              >
                Subtotal
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: pointTier(openDenom.value).color,
                }}
              >
                {fmtMoney(openDenom.value * openDenom.count)}
              </Typography>
            </Box>
          </Box>
        )}
      </ResponsiveSheet>

      <Dialog
        open={Boolean(vipPhotoUrl)}
        onClose={() => setVipPhotoUrl(null)}
        maxWidth="md"
      >
        {vipPhotoUrl && (
          <img
            src={vipPhotoUrl}
            alt="VIP"
            style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }}
          />
        )}
      </Dialog>

      <PointsAuditMode
        open={auditOpen}
        onClose={() => setAuditOpen(false)}
        onComplete={handleAuditComplete}
        points={auditPoints}
        duplicateFlags={duplicateFlags}
        decisions={decisions}
        auditIndex={auditIndex}
        onIndexChange={setAuditIndex}
        onDecision={handleDecision}
      />
    </Box>
  );
}

function Stat({ label, value, color }) {
  return (
    <Grid item xs={4}>
      <Box
        sx={{
          textAlign: 'center',
          background: TOKENS.slate50,
          borderRadius: 1,
          py: 0.75,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'monospace',
            color,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: 8,
            fontWeight: 700,
            textTransform: 'uppercase',
            color: TOKENS.slate500,
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Grid>
  );
}
